import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck } from 'lucide-react';

const QUESTIONS = [
  {
    id: 'q1',
    text: 'Sind personenbezogene Daten in HR-Prozessen dokumentiert (Zweck, Rechtsgrundlage)?',
  },
  {
    id: 'q2',
    text: 'Gibt es einen aktuellen Überblick, welche Systeme personenbezogene Daten verarbeiten?',
  },
  {
    id: 'q3',
    text: 'Sind Zugriffsrechte für sensible HR-Daten regelmäßig geprüft und nachvollziehbar?',
  },
  {
    id: 'q4',
    text: 'Werden Bewerber- und Mitarbeiterdaten getrennt und zweckgebunden verarbeitet?',
  },
  {
    id: 'q5',
    text: 'Gibt es einen definierten Prozess bei Datenanfragen (Auskunft, Löschung)?',
  },
];

const SelbstcheckComplianceResource = () => {
  const [searchParams] = useSearchParams();
  const q = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const [answers, setAnswers] = useState(() =>
    QUESTIONS.reduce((acc, q) => ({ ...acc, [q.id]: null }), {})
  );

  const score = useMemo(() => {
    const vals = Object.values(answers).filter((v) => v !== null);
    if (vals.length === 0) return null;
    const ja = vals.filter(Boolean).length;
    return Math.round((ja / vals.length) * 100);
  }, [answers]);

  const setAnswer = (id, value) => {
    setAnswers((prev) => ({ ...prev, [id]: value }));
  };

  const band =
    score === null
      ? null
      : score >= 80
        ? { label: 'Guter Stand', className: 'bg-emerald-50 border-emerald-200 text-emerald-900' }
        : score >= 50
          ? { label: 'Handlungsbedarf', className: 'bg-amber-50 border-amber-200 text-amber-900' }
          : { label: 'Priorität prüfen', className: 'bg-red-50 border-red-200 text-red-900' };

  return (
    <div className="space-y-8">
      <div>
        <Link
          to={q ? `/${q}` : '/'}
          className="inline-flex items-center text-sm font-medium text-hrf-navy hover:text-hrf-navy-800 mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zum Dashboard
        </Link>
        <div className="flex items-center gap-3">
          <div className="p-3 bg-violet-500/10 rounded-lg">
            <ClipboardCheck className="h-8 w-8 text-violet-600" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-hrf-navy">Selbstcheck Compliance</h1>
            <p className="mt-1 text-gray-600 text-sm">Kurzfragen zur Einordnung – keine Prüfung nach Norm</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 max-w-2xl space-y-6">
        {QUESTIONS.map((q, idx) => (
          <div key={q.id} className="border-b border-gray-100 last:border-0 pb-6 last:pb-0">
            <p className="text-sm font-medium text-hrf-navy mb-3">
              {idx + 1}. {q.text}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setAnswer(q.id, true)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  answers[q.id] === true
                    ? 'bg-hrf-navy text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Ja
              </button>
              <button
                type="button"
                onClick={() => setAnswer(q.id, false)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  answers[q.id] === false
                    ? 'bg-hrf-navy text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                Nein / unsicher
              </button>
            </div>
          </div>
        ))}

        {band && score !== null && (
          <div className={`rounded-lg border p-4 ${band.className}`}>
            <p className="text-xs uppercase tracking-wide opacity-80 mb-1">Ergebnis</p>
            <p className="text-lg font-semibold">{band.label}</p>
            <p className="text-sm mt-1 opacity-90">
              Übereinstimmung mit „Ja“: {score}% ({QUESTIONS.length} Fragen)
            </p>
          </div>
        )}

        <p className="text-xs text-gray-500 leading-relaxed">
          Der Check ersetzt keine Datenschutz-Folgenabschätzung oder ein Audit – aber er ist ein guter
          Gesprächsanstoß mit Datenschutz und Fachbereichen.
        </p>
      </div>
    </div>
  );
};

export default SelbstcheckComplianceResource;
